// Header/footer texts of the edunet site, copied 1:1 from https://edunet.works.
// The main site has only these languages; any other portal language falls back to English.

export const EDUNET_SITE = 'https://edunet.works';

export const edunetLanguages = ['en', 'de', 'uk', 'fr', 'es', 'ar'] as const;

export type EdunetLanguage = typeof edunetLanguages[number];

export const toEdunetLanguage = (lang: string): EdunetLanguage => {
  const found = edunetLanguages.find((item) => item === lang);
  return found || 'en';
};

interface EdunetTexts {
  nav: {
    welcome: string
    educationalNetwork: string
    jobPlacement: string
    learnGerman: string
    talentPool: string
    talents: string
    aboutUs: string
    contact: string
  }
  pages: string
  services: string
  companyInformation: string
  description: string
  germanCourses: string
  register: string
  managingDirector: string
  companyRegister: string
  court: string
  vatId: string
  rights: string
  privacy: string
  legalNotice: string
}

export const edunetTexts: Record<EdunetLanguage, EdunetTexts> = {
  en: {
    nav: {
      welcome: 'Welcome',
      educationalNetwork: 'Educational Network',
      jobPlacement: 'Job Placement',
      learnGerman: 'Learn German',
      talentPool: 'Talent Pool',
      talents: 'Talents',
      aboutUs: 'About Us',
      contact: 'Contact',
    },
    pages: 'Pages',
    services: 'Services',
    companyInformation: 'Company information',
    description: 'Connecting international skilled workers with German employers through language training, career preparation, and job placement.',
    germanCourses: 'German Language Courses',
    register: 'Register, it\'s free',
    managingDirector: 'Managing Director',
    companyRegister: 'Company Register',
    court: 'Court',
    vatId: 'VAT ID',
    rights: 'All rights reserved.',
    privacy: 'Privacy policy',
    legalNotice: 'Legal Notice',
  },
  de: {
    nav: {
      welcome: 'Willkommen',
      educationalNetwork: 'Bildungsnetzwerk',
      jobPlacement: 'Jobvermittlung',
      learnGerman: 'Deutsch lernen',
      talentPool: 'Talent Pool',
      talents: 'Talente',
      aboutUs: 'Über uns',
      contact: 'Kontakt',
    },
    pages: 'Seiten',
    services: 'Leistungen',
    companyInformation: 'Unternehmensangaben',
    description: 'Wir verbinden internationale Fachkräfte mit deutschen Arbeitgebern – durch Sprachausbildung, Karrierevorbereitung und Jobvermittlung.',
    germanCourses: 'Deutschkurse',
    register: 'Kostenlos registrieren',
    managingDirector: 'Geschäftsführerin',
    companyRegister: 'Handelsregister',
    court: 'Registergericht',
    vatId: 'USt-IdNr.',
    rights: 'Alle Rechte vorbehalten.',
    privacy: 'Datenschutz',
    legalNotice: 'Impressum',
  },
  uk: {
    nav: {
      welcome: 'Ласкаво просимо',
      educationalNetwork: 'Освітня мережа',
      jobPlacement: 'Працевлаштування',
      learnGerman: 'Вивчати німецьку',
      talentPool: 'Talent Pool',
      talents: 'Таланти',
      aboutUs: 'Про нас',
      contact: 'Контакт',
    },
    pages: 'Сторінки',
    services: 'Послуги',
    companyInformation: 'Дані компанії',
    description: 'Ми поєднуємо міжнародних фахівців з німецькими роботодавцями через мовну підготовку, підготовку до кар\'єри та послуги з працевлаштування.',
    germanCourses: 'Курси німецької мови',
    register: 'Зареєструватися безкоштовно',
    managingDirector: 'Генеральний директор',
    companyRegister: 'Торговий реєстр',
    court: 'Суд',
    vatId: 'ПДВ ID',
    rights: 'Усі права захищені.',
    privacy: 'Політика конфіденційності',
    legalNotice: 'Правова інформація',
  },
  fr: {
    nav: {
      welcome: 'Accueil',
      educationalNetwork: 'Réseau éducatif',
      jobPlacement: 'Placement professionnel',
      learnGerman: 'Apprendre l\'allemand',
      talentPool: 'Talent Pool',
      talents: 'Talents',
      aboutUs: 'À propos',
      contact: 'Contact',
    },
    pages: 'Pages',
    services: 'Services',
    companyInformation: 'Informations société',
    description: 'Nous mettons en relation des professionnels internationaux avec des employeurs allemands grâce à la formation linguistique, la préparation à la carrière et le placement professionnel.',
    germanCourses: 'Cours d\'allemand',
    register: 'S\'inscrire gratuitement',
    managingDirector: 'Directrice générale',
    companyRegister: 'Registre du commerce',
    court: 'Tribunal',
    vatId: 'N° TVA',
    rights: 'Tous droits réservés.',
    privacy: 'Politique de confidentialité',
    legalNotice: 'Mentions légales',
  },
  es: {
    nav: {
      welcome: 'Bienvenida',
      educationalNetwork: 'Red educativa',
      jobPlacement: 'Inserción laboral',
      learnGerman: 'Aprender alemán',
      talentPool: 'Talent Pool',
      talents: 'Talentos',
      aboutUs: 'Sobre nosotros',
      contact: 'Contacto',
    },
    pages: 'Páginas',
    services: 'Servicios',
    companyInformation: 'Datos de la empresa',
    description: 'Conectamos a profesionales internacionales con empleadores alemanes a través de formación lingüística, preparación profesional y servicios de inserción laboral.',
    germanCourses: 'Cursos de alemán',
    register: 'Registrarse gratis',
    managingDirector: 'Directora general',
    companyRegister: 'Registro mercantil',
    court: 'Tribunal',
    vatId: 'NIF intracomunitario',
    rights: 'Todos los derechos reservados.',
    privacy: 'Política de privacidad',
    legalNotice: 'Aviso legal',
  },
  ar: {
    nav: {
      welcome: 'الرئيسية',
      educationalNetwork: 'الشبكة التعليمية',
      jobPlacement: 'التوظيف',
      learnGerman: 'تعلّم الألمانية',
      talentPool: 'Talent Pool',
      talents: 'المواهب',
      aboutUs: 'من نحن',
      contact: 'اتصل بنا',
    },
    pages: 'الصفحات',
    services: 'الخدمات',
    companyInformation: 'بيانات الشركة',
    // the Arabic main site shows no description under the company name
    description: '',
    germanCourses: 'دورات اللغة الألمانية',
    register: 'التسجيل مجاناً',
    managingDirector: 'المديرة العامة',
    companyRegister: 'السجل التجاري',
    court: 'المحكمة',
    vatId: 'الرقم الضريبي لضريبة القيمة المضافة',
    rights: 'جميع الحقوق محفوظة.',
    privacy: 'سياسة الخصوصية',
    legalNotice: 'إشعار قانوني',
  },
};
